import React, { useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { useForm, Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAuth } from '@/hooks/useAuth'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { SkeletonForm } from '@/components/common/SkeletonForm'
import { ImageUpload } from '@/components/ImageUpload'
import { FormField } from '@/components/forms/FormField'
import { FormSelect } from '@/components/forms/FormSelect'
import { FormCheckbox } from '@/components/forms/FormCheckbox'
import { MaskedFormField } from '@/components/forms/MaskedFormField'
import { useDepartments, useRoles } from '@/hooks/useWorkforce'
import { useCreateEmployee } from '@/hooks/useWorkforceMutations'
import {
  employeeSchema,
  EmployeeFormData,
  formatCPF,
  formatPhone,
  formatCEP,
  BRAZILIAN_STATES,
} from '@/lib/validations/workforce'
import {
  ArrowLeft,
  Save,
  User,
  Building2,
  Briefcase,
  MapPin,
  Phone,
  Shield,
} from 'lucide-react'
import { toast } from 'sonner'

function CreateEmployeeContent() {
  const { user } = useAuth()
  const navigate = useNavigate()

  // Local state for image upload
  const [pictureKey, setPictureKey] = useState<string>('')
  const [pictureUrl, setPictureUrl] = useState<string>('')
  const [tempEmployeeId] = useState<string>(`temp-${Date.now()}`)

  // ✨ React Hook Form with Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    control,
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema) as any,
    mode: 'onBlur', // Validate on blur to show errors immediately
    shouldUnregister: false, // Keep values even when unmounted
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      cpf: '',
      departmentId: '',
      roleId: '',
      hireDate: new Date(),
      salary: 0,
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
      },
      emergencyContact: {
        name: '',
        relationship: '',
        phone: '',
      },
      benefits: {
        healthInsurance: false,
        dentalInsurance: false,
        visionInsurance: false,
        lifeInsurance: false,
        retirement401k: false,
        paidTimeOff: 20,
        mealVoucher: 0,
        transportVoucher: 0,
        gymMembership: false,
      },
    },
  })

  // Server state
  const { departments, loading: departmentsLoading } = useDepartments()
  const { roles, loading: rolesLoading } = useRoles()

  // Mutations
  const createEmployeeMutation = useCreateEmployee()

  const loading = departmentsLoading || rolesLoading
  const submitting = isSubmitting || createEmployeeMutation.isPending

  const selectedDepartmentId = watch('departmentId')

  // Form submission
  const onSubmit = async (data: EmployeeFormData) => {
    console.log('Form data on submit:', data)
    console.log('Phone value:', data.phone)
    console.log('Form errors:', errors)

    // FORCE validation check
    if (data.phone && !/^\(\d{2}\)\s\d{5}-\d{4}$/.test(data.phone)) {
      console.error('PHONE VALIDATION FAILED IN FORM!')
      return
    }

    try {
      const employeeData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        cpf: data.cpf,
        departmentId: data.departmentId,
        roleId: data.roleId,
        hireDate: data.hireDate,
        salary: data.salary,
        address: data.address,
        emergencyContact: data.emergencyContact,
        benefits: data.benefits,
        personalInfo: {
          name: `${data.firstName} ${data.lastName}`,
          phone: data.phone,
          pictureKey: pictureKey || undefined,
          pictureUrl: pictureUrl || undefined,
        },
      }

      console.log('>>> SENDING TO API:', JSON.stringify(employeeData, null, 2))

      const employee = await createEmployeeMutation.mutateAsync(employeeData)
      toast.success('Colaborador criado com sucesso!')
      navigate({ to: `/workforce/employees/${employee.employeeId}` })
    } catch (error) {
      console.error('Error creating employee:', error)
      const errorMessage = error instanceof Error ? error.message : 'Erro ao criar colaborador'
      toast.error(errorMessage)
    }
  }

  const departmentOptions = departments.map((dept) => ({
    value: dept.departmentId,
    label: dept.name,
  }))

  const roleOptions = roles.map((role) => ({
    value: role.roleId,
    label: role.name,
  }))

  if (loading) {
    return <SkeletonForm fields={12} twoColumn showHeader />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to={"/workforce/employees" as any}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </Link>
          <div>
            <h1 className="font-display text-3xl font-bold text-white">Novo Colaborador</h1>
            <p className="text-brand-gray">Adicione um novo colaborador ao sistema</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-6">
        {/* Basic Information */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">
              Informações Básicas
            </h2>
          </div>

          {/* Picture Upload */}
          <div className="mb-6">
            <ImageUpload
              employeeId={tempEmployeeId}
              currentImageUrl={pictureUrl}
              onUploadComplete={(key, url) => {
                setPictureKey(key)
                setPictureUrl(url)
              }}
              onError={(error) => console.error('Image upload error:', error)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Nome"
              name="firstName"
              register={register}
              error={errors.firstName}
              required
              placeholder="Digite o nome"
            />

            <FormField
              label="Sobrenome"
              name="lastName"
              register={register}
              error={errors.lastName}
              required
              placeholder="Digite o sobrenome"
            />

            <FormField
              label="Email"
              name="email"
              type="email"
              register={register}
              error={errors.email}
              required
              placeholder="email@exemplo.com"
            />

            <MaskedFormField
              label="Telefone"
              name="phone"
              control={control as any}
              error={errors.phone}
              placeholder="(11) 99999-9999"
              maskFunction={formatPhone}
            />

            <MaskedFormField
              label="CPF"
              name="cpf"
              control={control as any}
              error={errors.cpf}
              placeholder="000.000.000-00"
              maskFunction={formatCPF}
            />

            <FormField
              label="Data de Admissão"
              name="hireDate"
              type="date"
              register={register}
              error={errors.hireDate}
              required
            />
          </div>
        </Card>

        {/* Employment Information */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Briefcase className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">
              Informações de Emprego
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FormSelect
              label="Departamento"
              name="departmentId"
              register={register}
              error={errors.departmentId}
              required
              options={departmentOptions}
              placeholder="Selecione um departamento"
            />

            <FormSelect
              label="Cargo"
              name="roleId"
              register={register}
              error={errors.roleId}
              required
              options={roleOptions}
              placeholder="Selecione um cargo"
            />

            <FormField
              label="Salário (R$)"
              name="salary"
              type="number"
              register={register}
              error={errors.salary}
              placeholder="0.00"
              min={0}
              step={0.01}
            />
          </div>
        </Card>

        {/* Address */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">Endereço</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <FormField
                label="Rua"
                name="address.street"
                register={register}
                error={errors.address?.street}
                placeholder="Rua, número, complemento"
              />
            </div>

            <FormField
              label="Cidade"
              name="address.city"
              register={register}
              error={errors.address?.city}
              placeholder="Cidade"
            />

            <FormSelect
              label="Estado"
              name="address.state"
              register={register}
              error={errors.address?.state}
              options={BRAZILIAN_STATES}
              placeholder="Selecione o estado"
            />

            <MaskedFormField
              label="CEP"
              name="address.zipCode"
              control={control as any}
              error={errors.address?.zipCode}
              placeholder="00000-000"
              maskFunction={formatCEP}
            />
          </div>
        </Card>

        {/* Benefits */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">Benefícios</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <FormCheckbox
              label="Plano de Saúde"
              name="benefits.healthInsurance"
              register={register}
            />

            <FormCheckbox
              label="Plano Dental"
              name="benefits.dentalInsurance"
              register={register}
            />

            <FormCheckbox
              label="Plano de Visão"
              name="benefits.visionInsurance"
              register={register}
            />

            <FormCheckbox
              label="Seguro de Vida"
              name="benefits.lifeInsurance"
              register={register}
            />

            <FormCheckbox
              label="Previdência"
              name="benefits.retirement401k"
              register={register}
            />

            <FormField
              label="Dias de Férias"
              name="benefits.paidTimeOff"
              type="number"
              register={register}
              error={errors.benefits?.paidTimeOff}
              min={0}
              max={365}
            />
          </div>
        </Card>

        {/* Emergency Contact */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Phone className="w-5 h-5 text-brand-primary" />
            <h2 className="font-display text-xl font-semibold text-white">
              Contato de Emergência
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Nome"
              name="emergencyContact.name"
              register={register}
              error={errors.emergencyContact?.name}
              placeholder="Nome do contato"
            />

            <FormField
              label="Parentesco"
              name="emergencyContact.relationship"
              register={register}
              error={errors.emergencyContact?.relationship}
              placeholder="Ex: Mãe, Pai, Cônjuge"
            />

            <MaskedFormField
              label="Telefone"
              name="emergencyContact.phone"
              control={control as any}
              error={errors.emergencyContact?.phone}
              placeholder="(11) 99999-9999"
              maskFunction={formatPhone}
            />
          </div>
        </Card>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <Link to={"/workforce/employees" as any}>
            <Button variant="ghost" disabled={submitting}>
              Cancelar
            </Button>
          </Link>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Criando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Criar Colaborador
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default withPageProtection(CreateEmployeeContent, PAGES.WORKFORCE_EMPLOYEES_NEW)
